import { MemoId } from 'models/Memo';
import { setup, assign } from 'xstate';

export enum EditorMode {
  View = 'VIEW',
  Edit = 'EDIT',
}

export enum ClosingBehaviour {
  SaveOnClose = 'SAVE_ON_CLOSE',
  NoAction = 'NO_ACTION',
}

// State

export enum StateValue {
  Closed = 'CLOSED',
  Open = 'OPEN',
}

const CLOSED = StateValue.Closed;
const OPEN = StateValue.Open;

// Events

export enum EventType {
  RequestOpen = 'REQUEST_OPEN',
  RequestClose = 'REQUEST_CLOSE',
  RequestReset = 'REQUEST_RESET',
  RequestSetMode = 'REQUEST_SET_MODE',
}

type RequestOpenEditorEvent = {
  type: EventType.RequestOpen;
  memoId?: MemoId;
  mode?: EditorMode;
  closingBehavior?: ClosingBehaviour;
  canToggleMode?: boolean;
};

type RequestCloseEditorEvent = {
  type: EventType.RequestClose;
};

type RequestResetEvent = {
  type: EventType.RequestReset;
};

type RequestSetModeEvent = {
  type: EventType.RequestSetMode;
  mode?: EditorMode;
};

export type EditorMachineEvent =
  | RequestOpenEditorEvent
  | RequestCloseEditorEvent
  | RequestResetEvent
  | RequestSetModeEvent;

export interface EditorMachineContext {
  memoId?: MemoId;
  mode?: EditorMode;
  closingBehavior?: ClosingBehaviour;
  canToggleMode?: boolean;
}

export const editorMachine = setup({
  types: {
    context: {} as EditorMachineContext,
    events: {} as EditorMachineEvent,
  },
  actions: {
    reset: assign({
      memoId: null,
      mode: undefined,
      closingBehavior: undefined,
      canToggleMode: undefined,
    }),
    setMemoId: assign((_, params: { memoId?: MemoId }) => ({
      memoId: params.memoId,
    })),
    setClosingBehavior: assign(
      (_, params: { closingBehavior?: ClosingBehaviour }) => ({
        closingBehavior: params.closingBehavior,
      }),
    ),
    setCanToggleMode: assign((_, params: { canToggleMode?: boolean }) => ({
      canToggleMode: params.canToggleMode,
    })),
    setMode: assign((_, params: { mode?: EditorMode }) => ({
      mode: params.mode ?? EditorMode.View,
    })),
  },
}).createMachine({
  id: 'editor',
  context: {
    memoId: null,
  },
  initial: CLOSED,
  states: {
    [CLOSED]: {
      exit: 'reset',
      on: {
        [EventType.RequestOpen]: {
          target: OPEN,
          actions: [
            {
              type: 'setMemoId',
              params: ({ event }) => ({ memoId: event.memoId }),
            },
            { type: 'setMode', params: ({ event }) => ({ mode: event.mode }) },
            {
              type: 'setClosingBehavior',
              params: ({ event }) => ({
                closingBehavior: event.closingBehavior,
              }),
            },
            {
              type: 'setCanToggleMode',
              params: ({ event }) => ({ canToggleMode: event.canToggleMode }),
            },
          ],
        },
        [EventType.RequestReset]: {
          target: CLOSED,
          actions: 'reset',
        },
      },
    },
    [OPEN]: {
      exit: 'reset',
      on: {
        [EventType.RequestClose]: {
          target: CLOSED,
        },
        [EventType.RequestOpen]: {
          target: OPEN,
          actions: [
            {
              type: 'setMemoId',
              params: ({ event }) => ({ memoId: event.memoId }),
            },
            { type: 'setMode', params: ({ event }) => ({ mode: event.mode }) },
            {
              type: 'setClosingBehavior',
              params: ({ event }) => ({
                closingBehavior: event.closingBehavior,
              }),
            },
            {
              type: 'setCanToggleMode',
              params: ({ event }) => ({ canToggleMode: event.canToggleMode }),
            },
          ],
        },
        [EventType.RequestReset]: {
          target: CLOSED,
          actions: 'reset',
        },
        [EventType.RequestSetMode]: {
          actions: {
            type: 'setMode',
            params: ({ event }) => ({ mode: event.mode }),
          },
        },
      },
    },
  },
});
